import React, {useState, useEffect} from 'react';
import {Upload, Button, Image} from 'antd';
import {UploadOutlined, PlusOutlined, LoadingOutlined} from '@ant-design/icons';
import { toast } from 'sonner';

const defaultUrl = import.meta.env.VITE_SERVICE;

// Helper to generate a base64 preview for local files before upload.
const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// Helper to extract the final URL from a file object after upload.
const pickUrl = (file) => {
  if (!file) return null;
  if (file.response) {
    let {response} = file;
    if (typeof response === 'string') {
      try {
        response = JSON.parse(response);
      } catch (e) {
        return response;
      }
    }
    return response?.data?.url ?? response?.url ?? response?.Location ?? null;
  }
  if (file.url) {
    return file.url;
  }
  return null;
};

export default function PublicFile({value, onChange, multiple = false, children, image = false, ...uploadProps}) {
  const [internalFileList, setInternalFileList] = useState([]);
  const [preview, setPreview] = useState({open: false, image: ''});
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (!value) return;

    const incomingUrls = (Array.isArray(value) ? value : [value]).filter(v => typeof v === 'string');

    const currentInternalUrls = internalFileList
      .filter(file => file.status === 'done' && file.url) // Só olhamos arquivos prontos
      .map(file => file.url);

    const isUploading = internalFileList.some(file => file.status === 'uploading');
    if (isUploading) return;

    const isSameData =
      incomingUrls.length === currentInternalUrls.length &&
      incomingUrls.every((url, index) => url === currentInternalUrls[index]);

    if (isSameData) return;

    setInternalFileList(
      incomingUrls.map((url, i) => ({
        uid: `initial-${i}-${url}`, // UID único para evitar conflitos
        name: url.split('/').pop() || 'file',
        status: 'done',
        url
      }))
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = ({fileList: newFileList, file}) => {
    if (!multiple && newFileList.length > 0) {
      setLoading(file.status === 'uploading');
      newFileList = [file];
    }
    setInternalFileList(newFileList);
    const finalUrls = newFileList.map(pickUrl).filter(Boolean);
    const valueToSet = multiple ? finalUrls : (finalUrls[0] || null);
    if (onChange) {
      onChange(valueToSet);
    }
  };

  const handleError = (error) => {
    toast.error("Falha ao subir arquivo, tente novamente");
    console.error(error);
  }

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreview({open: true, image: file.url || file.preview});
  };

  const uploadContent = image ? (
    <button className={'border-0 bg-none h-full overflow-clip'} type="button">
      {loading ? <>
          <LoadingOutlined/>
          <div style={{marginTop: 8}}>Carregando...</div>
        </> :
        <>
          <PlusOutlined/>
          <div style={{marginTop: 8}}>Upload</div>
        </>
      }
    </button>
  ) : (
    children || <Button icon={<UploadOutlined/>}>Selecionar arquivos</Button>
  );

  return (
    <>
      <Upload
        multiple={multiple}
        action={`${defaultUrl}/public-file/`}
        name="file"
        accept={image && 'image/*'}
        headers={{Authorization: `Bearer ${localStorage.getItem('token')}`}}
        listType={image ? "picture-card" : "text"}
        {...uploadProps}
        fileList={internalFileList}
        onChange={handleChange}
        onError={handleError}
        onPreview={image ? handlePreview : undefined}
      >
        {(multiple || (!multiple && internalFileList.length < 1)) && uploadContent}
      </Upload>
      {preview.image && (
        <Image
          wrapperStyle={{display: 'none', margin: 0}}
          preview={{
            visible: preview.open,
            onVisibleChange: (visible) => setPreview({...preview, open: visible}),
            afterOpenChange: (visible) => !visible && setPreview({open: false, image: ''}),
          }}
          src={preview.image}
        />
      )}
    </>
  );
}
